import { useRef, useState } from "react";
import { CheckCircle2, Loader2, Paperclip, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useFormGuard } from "@/lib/formGuard";
import { FieldError, errCls, focusFirstError, isEmail, isPhone } from "@/lib/validate";

const EMPTY = { name: "", company: "", email: "", phone: "", country: "", language: "", doc_type: "", message: "" };
const ALLOWED = ["application/pdf", "image/jpeg", "image/png"];

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold text-muted-foreground mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

export function QuoteForm() {
  const { t, lang } = useT();
  const Q = t.quote;
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const { appendTo, refresh, Honeypot, GuardNotice } = useFormGuard();
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setFile(null);
    if (!ALLOWED.includes(f.type) || f.size > 15 * 1024 * 1024) { toast.error(Q.file_err); e.target.value = ""; return setFile(null); }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = t.errors.required;
    if (!form.email.trim()) er.email = t.errors.required; else if (!isEmail(form.email)) er.email = t.errors.email;
    if (form.phone.trim() && !isPhone(form.phone)) er.phone = t.errors.phone;
    if (!form.country.trim()) er.country = t.errors.required;
    if (!consent) er.consent = t.errors.consent;
    setErrors(er);
    if (Object.keys(er).length) { toast.error(t.errors.summary); focusFirstError(er); return; }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("lang", lang);
      if (file) fd.append("file", file);
      await appendTo(fd);
      await api.post("/legalization/quote", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      setForm(EMPTY); setFile(null); setConsent(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(err.response?.status === 429 ? t.errors.too_many : typeof d === "string" ? d : Q.err);
      if (err.response?.status === 400) refresh();
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-brand-teal/40 rounded-2xl p-8 text-center">
        <CheckCircle2 size={40} className="mx-auto text-brand-teal" />
        <h3 className="font-display font-bold text-xl mt-4">{Q.success_title}</h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-lg mx-auto">{Q.success_sub}</p>
        <button type="button" data-testid="quote-another" onClick={() => setDone(false)} className="mt-6 text-sm font-semibold border border-border rounded-full px-5 py-2.5 hover:bg-muted transition-colors">{Q.another}</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="quote-form" className="bg-white/85 dark:bg-slate-900/85 backdrop-blur border border-slate-200 dark:border-slate-800 rounded-2xl p-6 sm:p-8">
      <Honeypot />
      <span aria-hidden="true" className="block w-8 h-[3px] rounded-full bg-brand-red" />
      <h2 className="font-display font-bold text-lg md:text-xl tracking-tight mt-4">{Q.title}</h2>
      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{Q.sub}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={Q.name}><Input data-testid="quote-name-input" data-field="name" value={form.name} onChange={set("name")} className={`bg-background ${errCls(errors.name)}`} /><FieldError id="quote-name" msg={errors.name} /></Field>
        <Field label={Q.company}><Input data-testid="quote-company-input" value={form.company} onChange={set("company")} className="bg-background" /></Field>
        <Field label={Q.email}><Input data-testid="quote-email-input" data-field="email" type="email" value={form.email} onChange={set("email")} className={`bg-background ${errCls(errors.email)}`} /><FieldError id="quote-email" msg={errors.email} /></Field>
        <Field label={Q.phone}><Input data-testid="quote-phone-input" data-field="phone" value={form.phone} onChange={set("phone")} className={`bg-background ${errCls(errors.phone)}`} /><FieldError id="quote-phone" msg={errors.phone} /></Field>
        <Field label={Q.country}><Input data-testid="quote-country-input" data-field="country" value={form.country} onChange={set("country")} placeholder={Q.country_ph} className={`bg-background ${errCls(errors.country)}`} /><FieldError id="quote-country" msg={errors.country} /></Field>
        <Field label={Q.language}><Input data-testid="quote-language-input" value={form.language} onChange={set("language")} placeholder={Q.language_ph} className="bg-background" /></Field>
        <Field label={Q.doc_type} className="sm:col-span-2"><Input data-testid="quote-doctype-input" value={form.doc_type} onChange={set("doc_type")} placeholder={Q.doc_type_ph} className="bg-background" /></Field>
        <Field label={Q.message} className="sm:col-span-2"><Textarea data-testid="quote-message-input" rows={3} value={form.message} onChange={set("message")} className="bg-background resize-none" /></Field>
        <Field label={Q.file} className="sm:col-span-2">
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} data-testid="quote-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={pickFile} className="hidden" />
            <button type="button" data-testid="quote-file-button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-4 py-2 hover:bg-muted transition-colors"><Paperclip size={13} /> {Q.file_pick}</button>
            <span data-testid="quote-file-name" className="text-xs text-muted-foreground">{file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : Q.file_none}</span>
          </div>
        </Field>
      </div>
      <div data-field="consent" tabIndex={-1} className="mt-5">
        <label className="flex items-start gap-3 cursor-pointer text-xs text-muted-foreground leading-relaxed">
          <Checkbox data-testid="quote-consent-checkbox" checked={consent} onCheckedChange={(v) => { setConsent(v); setErrors({ ...errors, consent: undefined }); }} className={`mt-0.5 ${errors.consent ? "border-rose-500" : ""}`} />
          <span>{t.contact.form.consent} <a href="/biuro-wirtualne-warszawa/rodo" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 font-semibold text-foreground">{t.order.consent_link}</a>.</span>
        </label>
        <FieldError id="quote-consent" msg={errors.consent} />
      </div>
      <div className="mt-4"><GuardNotice /></div>
      <button type="submit" data-testid="quote-submit-button" disabled={sending} className="mt-5 inline-flex items-center gap-2 bg-brand-red hover:bg-red-700 disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors">
        {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {sending ? Q.sending : Q.submit}
      </button>
    </form>
  );
}
